import type { ActionFunction } from '~/types/route-args';
import type { FeaturedBrandPinToggleMutationVariables } from 'generated/graphql';
import { toggleFeaturedBrandPin } from '~/modules/home/api/toggle-featured-brand-pin.server';
import { z } from 'zod';
import * as zfd from 'zod-form-data';
import { UserErrorResponse } from '~/modules/root/graphql/responses';
import { clearBrandsCaches } from '@modules-rocco/professionals/api/get-brands-by-category.server';

const ToggleFeaturedBrandPinInputSchema = z.object({
    itemID: zfd.numeric(z.number()),
    pin: z.string(),
});

/**
 * API endpoint to toggle the featured brand pin status for a directory item on the home page.
 *
 * @param itemID - The ID of the directory item (professional, product, project, etc.) to toggle
 * @param pin - String representation of boolean ('true'/'false') indicating whether to pin or unpin
 * @returns The result of the toggle operation from the GraphQL mutation
 */
export const action: ActionFunction = async ({ request, context }) => {
    const formData = await request.formData();
    const zfdFormData = zfd.formData(ToggleFeaturedBrandPinInputSchema);
    const zfdResult = zfdFormData.safeParse(formData);
    if (!zfdResult.success) {
        throw new UserErrorResponse(zfdResult.error);
    }
    const variables: FeaturedBrandPinToggleMutationVariables = {
        itemID: zfdResult.data.itemID,
        pin: zfdResult.data.pin === 'true',
    };

    const result = await toggleFeaturedBrandPin({
        ...context,
        ...variables,
    });

    // Clear brands caches on successful mutation so the next request
    // to /actions/brands-by-category fetches fresh data.
    // Both error types (UserErrorResponse, ServerErrorResponse) extend Response,
    // so a plain object means the GraphQL mutation returned data (success).
    if (!(result instanceof Response)) {
        clearBrandsCaches();
    }

    return result;
};

export const TOGGLE_FEATURED_BRAND_PIN =
    '/remix-api/home/toggle-featured-brand-pin';
